import PropTypes from "prop-types"
import React, { useContext, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import {
  ArchiveContactAction,
  NewContractAction,
  NewReservationAction,
  RestoreContactAction,
} from "src/actions/contacts"
import ContractGroupModal from "src/main/Contact/ContractGroupModal"
import { ContactsContext } from "src/main/Contact/index"

import ClickablePhoneNumber from "src/components/ClickablePhoneNumber"
import DataTableV2 from "src/components/DataTableV2"
import DockwaAccountIndicator from "src/components/DockwaAccountIndicator"

import { archiveContact, restoreContact } from "src/api/Contacts"

import { useToast } from "src/hooks/use_toast"

import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

const COLUMNS = [
  {
    key: "name",
    width: "240px",
    header: "Name",
    sortable: true,
    cardRole: "title",
  },
  {
    key: "email",
    width: "320px",
    header: "Email",
    cardRole: "detail",
  },
  {
    key: "displayPhone",
    width: "180px",
    header: "Phone",
    cardRole: "detail",
    render: (row) => (
      <ClickablePhoneNumber phone={row.phone} displayPhone={row.displayPhone} />
    ),
  },
  {
    key: "linked",
    width: "180px",
    header: "Dockwa account",
    sortable: true,
    cardRole: "detail",
    render: (row) => (
      <DockwaAccountIndicator
        linked={row.isUser}
        linkIssue={row.linkIssue}
        tooltip
      />
    ),
  },
]

const ContactsTable = ({ table, status }) => {
  const { contractGroups } = useContext(ContactsContext)
  const [showModal, setShowModal] = useState(false)
  const [selectedContact, setSelectedContact] = useState(null)
  const marinaSlug = getCurrentMarinaSlug()
  const queryClient = useQueryClient()
  const showToast = useToast()
  const isActive = status === "active"

  const { mutate } = useMutation(
    (encodedId) =>
      isActive
        ? archiveContact(encodedId)
        : restoreContact(marinaSlug, encodedId),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries("contacts")
        showToast(data.message, { type: "success" })
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })
      },
    }
  )

  const activeActions = [
    NewReservationAction({ marinaSlug }),
    NewContractAction({
      onClickSingle: (row) => {
        setSelectedContact(row.encodedId)
        setShowModal(true)
      },
    }),
    ArchiveContactAction({
      onClickSingle: (row) => mutate(row.encodedId),
    }),
  ]

  const archivedActions = [
    RestoreContactAction({
      onClickSingle: (row) => mutate(row.encodedId),
    }),
  ]

  if (table.isError) {
    return (
      <div className="text-muted p-5 text-center">
        <h3 className="mb-5 text-lg font-semibold">Error loading captains</h3>
      </div>
    )
  }

  return (
    <>
      <DataTableV2
        name="contacts"
        columns={COLUMNS}
        data={table.records}
        actions={isActive ? activeActions : archivedActions}
        rowConfig={{
          getViewUrl: (row) =>
            `/manage/${marinaSlug}/contacts/${row.encodedId}`,
        }}
        rowCount={15}
        maxHeight="none"
        emptyState={
          <div className="text-muted p-5 text-center">
            <h3 className="mb-5 text-lg font-semibold">No captains found</h3>
          </div>
        }
        {...table.tableProps}
      />
      <ContractGroupModal
        contractGroups={contractGroups}
        isOpen={showModal}
        selectedContact={selectedContact}
        setIsOpen={setShowModal}
        setSelectedContact={setSelectedContact}
      />
    </>
  )
}

ContactsTable.propTypes = {
  table: PropTypes.object.isRequired,
  status: PropTypes.string.isRequired,
}

export default ContactsTable
